import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Headline, Text } from '@oneaudi/unified-web-components';
import { useSelector } from 'react-redux';
// @ts-ignore
// eslint-disable-next-line import/no-relative-packages
import FocusTrap from '../../../../node_modules/focus-trap-react/dist/focus-trap-react'; // or the exact path to 'focus-trap-react' if it's different
import {
  ModalWrapper,
  BlackBackground,
  ModalContainer,
  CTAsContainer,
  SecondaryCTA,
  TitleWrapper,
  PaymentsWrapper,
  Title,
  CloseButtonContainer,
  CloseModalButton,
  MainTextWrapper,
  PrimaryCTA,
  MSRP,
} from './Modal.styles';
import ModalPortal from './ModalPortal';
import useLockBodyScroll from '../../hooks/useLockBodyScroll';
import { PromotionType } from '../../types/offers';
import { parseDigitalLeadsData } from '../../utils/digitalLeadsFormat';
import { selectZipCode } from '../../reducers/zipCodeReducer';
import { priceFormat } from '../../utils/formatPrice';
import { CopyOne, TagLabel } from '../Common/Common.styles';
import formatModelName from '../../utils/formatModelName';
import PaymentsOptions from '../VehicleOffersList/VehicleTile/PricingOptions/PaymentsOptions';
import OffersTag from '../../assets/icons/OffersTag';
import CloseModalIcon from '../../assets/icons/CloseModalIcon';
import { COLORS } from '../../styles/variables';
import removeYearFromModelName from '../../utils/removeYearFromModelName';
import PaymentOptionsCa from '../VehicleOffersList/VehicleTileCa/PaymentOptionsCa';

interface ModalProps {
  offer: PromotionType;
  inventoryUrl: string;
  onCloseModal: () => void;
  dealerId?: string;
  isStackableOffers?: boolean;
  isModal?: boolean;
}

const Modal: React.FC<ModalProps> = ({
  offer,
  onCloseModal,
  inventoryUrl,
  dealerId,
  isStackableOffers,
  isModal,
}) => {
  useLockBodyScroll(true);

  const { t, i18n } = useTranslation();

  const isFrenchPage = i18n.language === 'fr';
  const {
    offerModelDetail,
    offerDisclaimer,
    offerDisclaimerFr,
    stackableOffers,
    programName,
    creditBonus,
    offerType,
    programToDate,
    customerDownPayment,
    monthlyPayment,
    term,
    aprRate,
  } = offer;

  const { modelCodeDescription, modelYear, trimline, vehicleType, modelName } = offerModelDetail;

  const stackOffer = stackableOffers[0];
  const isBonusOffers = offerType === 'credit' || isStackableOffers;

  const isCpoVehicle = vehicleType === 'USED-CAR';

  const { confirmedZipcode, market } = useSelector(selectZipCode);

  const isCaMarket = market === 'CA';

  const msrp = priceFormat(offer?.offerModelDetail?.msrp);
  const trimmedModelName =
    offer && isCaMarket ? modelName : removeYearFromModelName(modelYear, modelCodeDescription);
  const modelFullName = isCaMarket ? `${modelName}` : formatModelName(modelCodeDescription);

  const digitalLeadsInfo = useMemo(() => {
    const dataInfo = parseDigitalLeadsData(offer, isCaMarket, confirmedZipcode, dealerId);
    return dataInfo;
  }, [offer, confirmedZipcode, dealerId, isCaMarket]);

  const programNameCopy = stackOffer?.programName ?? programName;

  const offerLabel = useMemo(() => {
    // if it's a stack offer use the program name of stack offer otherwise use program name from actual offer
    if (programNameCopy === 'AUD_EV_CREDIT1') return 'EV Lease Bonus';
    if (programNameCopy === 'AUD_SOA_COMBINED') return 'Season of Audi Bonus + Audi Credit';
    if (programNameCopy === 'AUD_SALES_EVENT') return 'Season of Audi Customer Bonus';
    if (programNameCopy === 'AUD_CUSTOMER_CREDIT' || !!stackOffer) return 'Customer Bonus';
    if (programNameCopy === 'AUD_COSTCO_SELECT_PROGRAM') return 'Costco Select Bonus';
    return 'Audi Credit';
  }, [programNameCopy, stackOffer]);

  const disclaimer = isFrenchPage && offerDisclaimerFr ? offerDisclaimerFr : offerDisclaimer;

  const creditOfferTitle = useMemo(() => {
    const bonusValue = stackOffer ? priceFormat(stackOffer?.creditBonus) : priceFormat(creditBonus);
    return `${bonusValue} ${offerLabel}`;
  }, [creditBonus, offerLabel, stackOffer]);

  const renderLeaseDetails = useMemo(() => {
    return (
      <div>
        <div>
          <TagLabel data-testid="offer-details-tag">
            {t(`promo.offers.details.${offerType}`)}
          </TagLabel>
        </div>
        <TitleWrapper>
          <Headline
            tag="h4"
            variant={{
              order: '4',
              type: 'headline',
              weight: 'normal',
            }}
          >
            {t('promo.offers.details.offerDetails')}
          </Headline>
          <Headline
            data-testid="offer-details-name"
            tag="h2"
            variant={{
              order: '2',
              type: 'headline',
              weight: 'bold',
            }}
          >
            {modelFullName}
          </Headline>
        </TitleWrapper>
        {isCaMarket && !isCpoVehicle && (
          <MSRP>
            <Text
              variant={{
                order: '2',
                style: 'normal',
              }}
            >
              {t('promo.tile.msrp')}
            </Text>
            <Headline
              tag="h4"
              variant={{
                order: '4',
                type: 'headline',
                weight: 'bold',
              }}
            >
              {msrp}
            </Headline>
          </MSRP>
        )}

        <PaymentsWrapper>
          {isCaMarket ? (
            <PaymentOptionsCa offer={offer} isModal={isModal} />
          ) : (
            <PaymentsOptions offer={offer} />
          )}
        </PaymentsWrapper>
      </div>
    );
  }, [isCpoVehicle, isModal, modelFullName, msrp, offer, offerType, t, isCaMarket]);

  const renderCreditDetails = useMemo(() => {
    return (
      <div>
        <div>
          <TagLabel data-testid="bonus-details-tag">
            <OffersTag />
            {t('promo.offers.details.creditTag')}
          </TagLabel>
        </div>
        <TitleWrapper>
          <Title data-testid="bonus-details-title" isBold>
            {creditOfferTitle}
          </Title>
        </TitleWrapper>
      </div>
    );
  }, [creditOfferTitle, t]);

  return (
    <ModalPortal>
      <FocusTrap
        focusTrapOptions={{
          initialFocus: true,
          escapeDeactivates: true,
        }}
      >
        <ModalWrapper>
          <BlackBackground onClick={onCloseModal} aria-label={t('promo.modal.backdrop')} />
          <ModalContainer
            aria-label={t('promo.modal.label')}
            aria-modal="true"
            data-modal-title={t('promo.modal.label')}
            data-promotion-type={offerType}
            data-promotion-offerdate={programToDate}
            data-promotion-credit={isBonusOffers ? stackOffer?.creditBonus : creditBonus}
            data-promotion-lease-downpayment={customerDownPayment?.toString() ?? 'null'}
            data-promotion-lease-payment={monthlyPayment?.toString() ?? 'null'}
            data-promotion-lease-termlength={offerType === 'lease' ? term : 'null'}
            data-promotion-finance-apr={offerType === 'finance' ? aprRate : 'null'}
            data-promotion-finance-termlength={offerType === 'finance' ? term : 'null'}
            data-vehicle-model-name={trimmedModelName}
            data-vehicle-model-year={modelYear}
            data-vehicle-price={msrp !== '-' ? msrp : 'null'}
            data-vehicle-trim={trimline}
          >
            <CloseButtonContainer>
              <CloseModalButton
                onClick={onCloseModal}
                aria-label={t('promo.modal.close')}
                data-testid="close-modal-button"
              >
                <CloseModalIcon />
              </CloseModalButton>
            </CloseButtonContainer>
            {isBonusOffers ? renderCreditDetails : renderLeaseDetails}
            <MainTextWrapper>
              <CopyOne
                color={COLORS.black}
                data-testid={
                  isBonusOffers ? 'bonus-details-description' : 'offer-details-description'
                }
              >
                {isStackableOffers && stackOffer?.offerDisclaimer
                  ? stackOffer.offerDisclaimer
                  : disclaimer}
              </CopyOne>
            </MainTextWrapper>
            <div>
              <CopyOne
                color={COLORS.grey80}
                data-testid={
                  isBonusOffers ? 'bonus-details-expiration-date' : 'offer-details-expiration-date'
                }
              >
                {t('promo.offers.details.validationUntil', {
                  date: `${offer.programToDate}`,
                })}
              </CopyOne>
            </div>
            <CTAsContainer>
              <PrimaryCTA
                href={inventoryUrl}
                data-testid="offer-details-inventory-cta"
                tabIndex={0}
                target="_blank"
              >
                {t('promo.offers.cta.inventory')}
              </PrimaryCTA>
              <SecondaryCTA
                id="digital-leads"
                data-info={digitalLeadsInfo}
                data-testid="offer-details-contact-cta"
                onClick={onCloseModal}
              >
                {t('promo.offers.cta.contact')}
              </SecondaryCTA>
            </CTAsContainer>
          </ModalContainer>
        </ModalWrapper>
      </FocusTrap>
    </ModalPortal>
  );
};

export default Modal;
